"use client";

import { Link, useLocation } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import { useEffect, useState } from "react";
import axios from "../api/axios";
import logo from "../assets/Artboard4.jpg";
import {
  Menu,
  X,
  Bell,
  Home,
  Users,
  Upload,
  Shield,
  FileText,
  UserPlus,
  BarChart3,
  ChevronDown,
  Package,
} from "lucide-react";

function NavBar() {
  const [user, setUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showMoreNav, setShowMoreNav] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchUserAndPermissions = async () => {
      try {
        const userRes = await axios.get("/api/user", { withCredentials: true });
        setUser(userRes.data);

        const permissionsRes = await axios.get("/api/permissions", {
          withCredentials: true,
        });
        setPermissions(permissionsRes.data.permissions || []);
        setModules(permissionsRes.data.modules || []);
      } catch (err) {
        console.error("Error loading user or permissions");
        setUser(null);
        setPermissions([]);
        setModules([]);
      }
    };

    fetchUserAndPermissions();
  }, []);

  const canView = (moduleName) => {
    const module = modules.find(
      (m) => m.name.toLowerCase() === moduleName.toLowerCase()
    );
    if (!module) return false;

    return permissions.some(
      (p) =>
        p.module_id === module.id &&
        p.role_id === user?.role_id &&
        p.can_view === 1
    );
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get("/api/notifications");
      if (Array.isArray(res.data)) {
        setNotifications(res.data);
      } else {
        console.warn("API returned unexpected data:", res.data);
        setNotifications([]);
      }
    } catch (err) {
      console.error("Failed to fetch notifications", err);
      setNotifications([]);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".notification-wrapper")) {
        setShowDropdown(false);
      }
      if (!e.target.closest(".more-nav-wrapper")) {
        setShowMoreNav(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleBellClick = async () => {
    const willOpen = !showDropdown;
    setShowDropdown(willOpen);

    if (willOpen) {
      try {
        await axios.post("/api/notifications/mark-as-read");
        await fetchNotifications();
      } catch (err) {
        console.error("Failed to mark notifications as read", err);
      }
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const navigationItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: BarChart3,
      permission: "Admin Dashboard",
      show: canView("Admin Dashboard"),
    },
    {
      name: "User Dashboard",
      path: "/user",
      icon: Home,
      permission: "User Dashboard",
      show: canView("User Dashboard"),
    },
    {
      name: "Upload Files",
      path: "/uploadPage",
      icon: Upload,
      permission: "Upload files module",
      show: canView("Upload files module"),
    },
    {
      name: "Search",
      path: "/files",
      icon: FileText,
      permission: "Files module",
      show: canView("Files module"),
    },
    {
      name: "Container Status",
      path: "/container-status",
      icon: Package,
      permission: "Container Status module",
      show: canView("Container Status module"),
    },
    {
      name: "Teams",
      path: "/teammodule",
      icon: Users,
      permission: "Teams module",
      show: canView("Teams module"),
    },
    {
      name: "Add User",
      path: "/register",
      icon: UserPlus,
      permission: "Register module",
      show: canView("Register module"),
    },
    {
      name: "Users List",
      path: "/users",
      icon: Users,
      permission: "Users list module",
      show: canView("Users list module"),
    },
    {
      name: "File Types",
      path: "/file-type",
      icon: FileText,
      permission: "Files type module",
      show: canView("Files type module"),
    },
    {
      name: "Change Password",
      path: "/change-password",
      icon: Users,
      permission: "Change Password module",
      show: canView("Change Password module"),
    },
    {
      name: "Permissions",
      path: "/permissionspanel",
      icon: Shield,
      permission: "Permission module",
      show: canView("Permission module"),
    },
  ];

  const visibleItems = navigationItems.filter((item) => item.show);
  const primaryItems = visibleItems.slice(0, 5);
  const moreItems = visibleItems.slice(5);

  const getTimeAgo = (dateString) => {
    const createdDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - createdDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60)
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  return (
    <nav className="bg-white shadow-lg border-b-2 border-orange-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <div className="flex items-center space-x-3">
              <img
                src={logo}
                alt="KSP Inc Logo"
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div
                className="w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center"
                style={{ display: "none" }}
              >
                <span className="text-white font-bold text-sm">K</span>
              </div>
              <div>
                <span className="text-2xl font-bold text-gray-900">
                  KSPData
                </span>
                <p className="text-xs text-gray-500 -mt-1">Digital Storage</p>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-center space-x-1">
              {!user && (
                <Link
                  to="/"
                  className="text-gray-600 hover:text-orange-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Login
                </Link>
              )}

              {user &&
                primaryItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-orange-100 text-orange-700 border border-orange-200"
                          : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                      }`}
                    >
                      <Icon size={16} />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}

              {/* More dropdown for additional items */}
              {moreItems.length > 0 && (
                <div className="relative more-nav-wrapper">
                  <button
                    onClick={() => setShowMoreNav(!showMoreNav)}
                    className="flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                  >
                    <span>More</span>
                    <ChevronDown
                      size={16}
                      className={`transform transition-transform ${
                        showMoreNav ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showMoreNav && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                      {moreItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                              isActive
                                ? "bg-orange-50 text-orange-700"
                                : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                            }`}
                            onClick={() => setShowMoreNav(false)}
                          >
                            <Icon size={16} />
                            <span>{item.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {/* User info */}
            {user && (
              <div className="hidden md:flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {user.name}
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
                <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {user.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            {/* Notifications */}
            {user && (
              <div className="relative notification-wrapper">
                <button
                  onClick={handleBellClick}
                  className="relative p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center font-medium">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Notifications
                        </h3>
                        {unreadCount > 0 && (
                          <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                            {unreadCount} new
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {(showAll
                        ? notifications.slice(0, 10)
                        : notifications.slice(0, 5)
                      ).map((note, index) => (
                        <div
                          key={index}
                          className={`p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                            !note.is_read ? "bg-orange-50" : ""
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div
                              className={`w-2 h-2 rounded-full mt-2 ${
                                !note.is_read ? "bg-orange-500" : "bg-gray-300"
                              }`}
                            ></div>
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900">
                                {note.title}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {note.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-2">
                                {getTimeAgo(note.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}

                      {notifications.length > 5 && (
                        <button
                          className="w-full p-3 text-orange-600 text-sm font-medium hover:bg-orange-50 transition-colors"
                          onClick={() => setShowAll((prev) => !prev)}
                        >
                          {showAll
                            ? "Show less"
                            : `View all ${notifications.length} notifications`}
                        </button>
                      )}

                      {notifications.length === 0 && (
                        <div className="p-8 text-center">
                          <Bell
                            size={32}
                            className="mx-auto text-gray-300 mb-2"
                          />
                          <p className="text-sm text-gray-500">
                            No notifications yet
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Logout button */}
            {user && <LogoutButton />}

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-colors"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {user && (
              <div className="flex items-center space-x-3 px-3 py-3 border-b border-gray-100 mb-2">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {user.name}
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
              </div>
            )}

            {!user && (
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 block px-3 py-2 rounded-md text-base font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Home size={20} />
                <span>Login</span>
              </Link>
            )}

            {user &&
              visibleItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                      isActive
                        ? "bg-orange-100 text-orange-700 border border-orange-200"
                        : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
          </div>
        </div>
      )}
    </nav>
  );
}

export default NavBar;
